import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from './use-toast';
import { QUERY_KEYS } from '@viome/viome-base/src/api/config';
import { questionnaireV2 } from '@viome/viome-base/src/api/Endpoints';
import { useActivePreRnrKitId } from '@viome/viome-base/src/api/activeKit';
import { getNextSectionInfo } from '@viome/viome-base/src/utils/questionnaireUtils';
import { UseFormReturn } from 'react-hook-form';
import { useSubmitAnswersV2 } from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.v2.query';
import {
  AnswerWithValue,
  QuestionnaireFormData,
  QuestionnaireGroupResponse,
} from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.model';
import dayjs from 'dayjs';
import * as P from '@/constants/paths';
import { useState } from 'react';

interface UseQuestionnaireSubmissionProps {
  filterFormData: (formData: QuestionnaireFormData) => QuestionnaireFormData;
  fromCollectSample: boolean;
  questionnaireGroupVersionId?: number;
  methods: UseFormReturn<QuestionnaireFormData, unknown, undefined>;
  returnNavigate: string;
}

export const useQuestionnaireSubmission = ({
  filterFormData,
  fromCollectSample,
  questionnaireGroupVersionId,
  methods,
  returnNavigate,
}: UseQuestionnaireSubmissionProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutate: submitAnswers, isPending: isSubmitting } = useSubmitAnswersV2();
  const [isRefetching, setIsRefetching] = useState(false);
  // Same kit the v2 questionnaire reads/writes — used to target the v2 caches.
  const kitId = useActivePreRnrKitId();
  const handleSubmission = async (
    formData: QuestionnaireFormData,
    saveAndExit?: boolean,
  ) => {
    // Filter the form data to only include visible questions and valid values
    const filteredData = filterFormData(formData);

    const answers = Object.entries(filteredData).map(([questionId, value]) => {
      const baseAnswer = {
        question_id: parseInt(questionId, 10),
      };

      if (typeof value === 'string') {
        return {
          ...baseAnswer,
          single_choice_answer: value,
        };
      } else if (value instanceof Date) {
        return {
          ...baseAnswer,
          single_choice_answer: dayjs(value).format('YYYY-MM-DD'),
        };
      } else if (Array.isArray(value)) {
        return {
          ...baseAnswer,
          multi_choice_answer: value,
        };
      } else if (
        typeof value === 'object' &&
        value !== null &&
        'value' in value &&
        'unit' in value
      ) {
        return {
          ...baseAnswer,
          answer_with_value: [
            { ...value, value: String(value.value) } as AnswerWithValue,
          ],
        };
      } else if (
        typeof value === 'object' &&
        value !== null &&
        'hours' in value &&
        'minutes' in value
      ) {
        const timeValue = value as { hours: number; minutes: number };
        const timerAnswers: AnswerWithValue[] = [];

        timerAnswers.push({
          unit: 'hours per day',
          value: timeValue.hours > 0 ? String(timeValue.hours) : '0',
        });

        timerAnswers.push({
          unit: 'minutes per day',
          value: timeValue.minutes > 0 ? String(timeValue.minutes) : '0',
        });

        return {
          ...baseAnswer,
          answer_with_value: timerAnswers,
        };
      } else if (
        typeof value === 'object' &&
        value !== null &&
        'can_skip_value' in value
      ) {
        return {
          ...baseAnswer,
          can_skip_value: (value as { can_skip_value: boolean }).can_skip_value,
        };
      } else {
        // Fallback for other types (convert to string)
        return {
          ...baseAnswer,
          single_choice_answer: String(value),
        };
      }
    });

    const bodyParams = fromCollectSample
      ? {
          onboarding_questions_only: true,
          save_and_exit: saveAndExit,
        }
      : {
          question_group_version_id: questionnaireGroupVersionId,
          save_and_exit: saveAndExit,
        };

    submitAnswers(
      {
        data: { answers },
        params: bodyParams,
      },
      {
        onSuccess: async () => {
          // Reset form and invalidate queries
          methods.reset();
          // Invalidate questionnaire questions (v2 — kit-scoped). The v2 read
          // key is [KEY, kitId, discriminator]; mirror the original targeting
          // with kitId inserted at position 2.
          queryClient.invalidateQueries({
            queryKey: fromCollectSample
              ? [
                  QUERY_KEYS.QUESTIONNAIRE_QUESTION_LIST_V2,
                  kitId,
                  fromCollectSample,
                ]
              : [
                  QUERY_KEYS.QUESTIONNAIRE_QUESTION_LIST_V2,
                  kitId,
                  questionnaireGroupVersionId,
                ],
          });

          // Also invalidate journey states when fromCollectSample is true (v2)
          if (fromCollectSample) {
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.JOURNEY_STATES_V2, kitId],
            });
          }

          try {
            // Set refetching state to true before fetching new data
            setIsRefetching(true);

            // The v2, kit-scoped group-list cache the landing page reads from.
            const groupListKey = [
              QUERY_KEYS.QUESTIONNAIRE_GROUP_LIST_V2,
              kitId,
            ];

            // Get the old data before invalidating queries to check completion status
            const oldData =
              queryClient.getQueryData<QuestionnaireGroupResponse>(
                groupListKey,
              );
            const oldDataIsCompleted =
              oldData?.no_of_answered_questions && oldData?.no_of_questions
                ? oldData.no_of_answered_questions >= oldData.no_of_questions
                : false;

            // Invalidate and refetch questionnaire group list to get latest data
            await queryClient.invalidateQueries({
              queryKey: groupListKey,
            });

            if (saveAndExit) {
              navigate(returnNavigate);
            } else {
              // Fetch the latest data
              const latestData = await queryClient.fetchQuery({
                queryKey: groupListKey,
                queryFn: async () => {
                  const response = await questionnaireV2.getQuestionGroupList(
                    kitId as string,
                  );
                  return response.data;
                },
              });

              const sections = latestData?.question_group || [];

              if (sections.length > 0 && questionnaireGroupVersionId) {
                const { nextSection, isLast } = getNextSectionInfo(
                  sections,
                  questionnaireGroupVersionId,
                );

                const isLastQuestion = oldDataIsCompleted ? false : !!isLast;

                if (nextSection || (!!oldDataIsCompleted)) {
                  navigate(P.QUESTIONNAIRE_SUCCESS_PATH, {
                    state: {
                      currentGroupId: questionnaireGroupVersionId,
                      nextSection,
                      isLast: isLastQuestion,
                      isUpdate: !!oldDataIsCompleted,
                      enable_read_only_mode:
                        latestData?.enable_read_only_mode || false,
                    },
                  });
                } else if (isLastQuestion) {
                  navigate(P.QUESTIONNAIRE_COMPLETED_PATH);
                } else {
                  navigate(returnNavigate);
                }
              } else {
                navigate(returnNavigate);
              }
            }
          } catch (error) {
            console.error('Error fetching latest data:', error);
            navigate(returnNavigate);
          } finally {
            // Reset refetching state regardless of success or failure
            setIsRefetching(false);
          }
        },
        onError: () => {
          toast({
            title: 'Error',
            description: `Failed to ${saveAndExit ? 'save' : 'submit'} questionnaire. Please try again.`,
            variant: 'destructive',
          });
          if (saveAndExit) {
            navigate(returnNavigate);
          }
        },
      },
    );
  };

  return {
    handleSubmission,
    isSubmitting,
    isRefetching,
  };
};
